(()=>{
'use strict';
function parseDimension(text) {
  const value=String(text).trim().replace(/[−–]/g,'-').replace(/⁄/g,'/');
  const mm=value.match(/([\d.]+)\s*mm/i);
  if(mm)return Number(mm[1])/25.4;
  const normalized=value.replace(/["″”“]|\bin(?:ches)?\b/gi,'').replace(/[()]/g,'').trim();
  if(!/^\d+(?:\.\d+)?(?:\s*[- ]\s*\d+\/\d+|\/\d+)?$/.test(normalized))return NaN;
  return normalized.split(/\s*[- ]\s*/).reduce((total,part)=>{const [n,d]=part.split('/').map(Number);return total+n/(d||1)},0);
}
function normalizeCatalog(config) {
  const products=[];
  for(const family of config.families||[]) {
    const product=family.product;if(!product)continue;
    const optionNames=product.options.map(o=>typeof o==='string'?o:o.name);
    const sizeIndex=optionNames.findIndex(n=>n.toLowerCase()===config.sizeOption.toLowerCase());
    const packIndex=optionNames.findIndex(n=>n.toLowerCase()===config.packOption.toLowerCase());
    const groups=new Map();
    for(const variant of product.variants||[]) {
      const size=variant.options?.[sizeIndex] || variant.title;
      if(!groups.has(size))groups.set(size,[]);groups.get(size).push(variant);
    }
    for(const [size,variants]of groups){
      const parts=size.split(/[×x]/i);
      const length=parts.length===2?parseDimension(parts[1]):NaN;
      const packs=packIndex<0?[]:[...new Set(variants.map(v=>Number(String(v.options[packIndex]).replace(/,/g,'').match(/\d+/)?.[0])).filter(n=>Number.isInteger(n)&&n>0))].sort((a,b)=>a-b);
      products.push({family:family.key,diameter:parts[0].trim(),lengthText:parts[1]?.trim()||size,length,size,key:family.key+'|'+size,productId:product.id,productTitle:product.title,url:product.url,variants,packIndex,packs,available:variants.some(v=>v.available),quotePacks:family.quotePacks});
    }
  }
  return products;
}
function purchaseEntries(product, lines, pricing) {
  if(!product)throw new Error('Choose a product size.');
  return lines.map(line=>{
    if(!Number.isSafeInteger(line.count)||line.count<1||!Number.isSafeInteger(line.size)||line.size<1)throw new Error('Enter a whole pack quantity greater than zero.');
    let variant,quantity;
    if(product.packIndex>=0){const matches=product.variants.filter(v=>Number(String(v.options[product.packIndex]).replace(/,/g,'').match(/\d+/)?.[0])===line.size);variant=matches.length===1?matches[0]:null;quantity=line.count;}
    else if(pricing==='per_piece'){variant=product.variants.length===1?product.variants[0]:null;quantity=line.count*line.size;}
    if(!variant)throw new Error('This pack is available by quote.');
    if(!variant.available)throw new Error('This size or pack is currently unavailable.');
    if(!(Number(variant.price)>0))throw new Error('Request pricing for this size and pack.');
    if(!Number.isSafeInteger(quantity)||quantity>10000000)throw new Error('Please request a quote for this quantity.');
    const rule=variant.quantity_rule;
    if(rule&&(quantity<(rule.min||1)||(rule.max&&quantity>rule.max)||quantity%(rule.increment||1)))throw new Error('This quantity does not meet the product order requirements.');
    return {id:variant.id,quantity,price:Number(variant.price),properties:{'Pack size':line.size+' pcs','Pack quantity':String(line.count)}};
  });
}

function createEngine(source = {}) {

  const EPS = 1e-8;
  const ceil = n => Math.ceil(n - EPS);
  function inches(value) {
    return String(value).split('-').reduce((sum, term) => {
      const f = term.split('/').map(Number);
      return sum + (f.length === 2 ? f[0] / f[1] : f[0]);
    }, 0);
  }
  const products = source.products || [];
  const ranges = {};
  const scenes = {
    wood: 'Wood deck boards on wood joists',
    composite: 'Composite deck boards on wood joists',
    steel: 'Wood deck boards on steel joists',
    hiddenwood: 'Hidden deck clips on wood joists',
    hiddensteel: 'Hidden deck clips on steel joists',
    fence: 'Vertical fence pickets',
    horizontal: 'Horizontal fence boards',
    trim: 'Trim and moulding',
    join: 'Ordinary wood or furniture joints',
    faceframe: 'Joining cabinet face frames',
    cabinet: 'Mounting cabinets to wall studs',
    structural: 'Structural connections — existing screw schedule'
  };
  const defaults = {
    wood: { length: 12, width: 10, boardWidth: 5.5, thickness: 1, gap: .125, spacing: 16, boardLength: 0, backing: 5.5 },
    composite: { length: 12, width: 10, boardWidth: 5.5, thickness: 1, gap: .1875, spacing: 16, boardLength: 0, backing: 5.5 },
    steel: { length: 12, width: 10, boardWidth: 5.5, thickness: 1, gap: .125, spacing: 16, boardLength: 0, steelThickness: .075 },
    hiddenwood: { length: 12, width: 10, boardWidth: 5.5, thickness: 1, gap: .1875, spacing: 16, boardLength: 0, backing: 5.5, clipSeat: .125, clipMaterial: 'Black Polymer' },
    hiddensteel: { length: 12, width: 10, boardWidth: 5.5, thickness: 1, gap: .1875, spacing: 16, boardLength: 0, steelThickness: .075, clipSeat: .125, clipMaterial: 'Black Polymer' },
    fence: { length: 40, height: 6, boardWidth: 5.5, thickness: .625, gap: .25, rails: 3, screwsPerPoint: 2, backing: 1.5 },
    horizontal: { length: 40, height: 6, boardWidth: 5.5, thickness: .75, gap: .25, spacing: 96, boardLength: 8, screwsPerPoint: 2, backing: 3.5 },
    trim: { length: 40, boardWidth: 3.5, thickness: .75, boardLength: 8, spacing: 16, screwsPerPoint: 1, wallLayer: .5, backing: 3.5, allowStandardHead: true },
    join: { connections: 20, screwsPerPoint: 2, thickness: .75, backing: 1.5 },
    faceframe: { connections: 4, screwsPerPoint: 3, thickness: 1.5, backing: 1.5, allowStandardHead: true },
    cabinet: { units: 4, studs: 2, screwsPerPoint: 2, thickness: .75, wallLayer: .5, backing: 3.5, approvedProduct: '' },
    structural: { connections: 10, screwsPerPoint: 4, approvedProduct: '' }
  };
  function fail(field, message) { return { status: 'invalid', field, message, items: [], metadata: {} }; }
  function packOptions(family, product) {
    const configured=source.families?.find(f=>f.key===family);
    const raw=product?.packs?.length?product.packs:configured?.quotePacks;
    const sizes=[...new Set((Array.isArray(raw)?raw:String(raw||'').split(',')).map(Number).filter(n=>Number.isSafeInteger(n)&&n>0&&n<=100000))].sort((a,b)=>a-b);
    return sizes;
  }
  function planPacks(target,family,mode='mix',product) {
    if(!target)return {lines:[],purchased:0,packs:0,surplus:0};
    const sizes=packOptions(family,product);
    if(!sizes.length)return {lines:[],purchased:0,packs:0,surplus:0,unconfigured:true};
    const names=['Handy Pack','Homepak','Engineer Tub'];
    if(mode!=='mix'){
      const index=names.indexOf(mode),size=sizes[index];
      if(!size)return {lines:[],purchased:0,packs:0,surplus:0,unconfigured:true};
      const count=ceil(target/size);return {lines:[{name:names[index],size,count}],purchased:count*size,packs:count,surplus:count*size-target};
    }
    const gcd=(a,b)=>b?gcd(b,a%b):a,unit=sizes.reduce(gcd),needed=ceil(target/unit),coins=sizes.map(n=>n/unit);
    const limit=needed+Math.max(...coins),counts=new Map();
    if(limit>250000){const size=sizes.at(-1);counts.set(size,ceil(target/size));}
    else{
      const dp=new Int32Array(limit+1),prev=new Int32Array(limit+1);dp.fill(2147483647);dp[0]=0;
      for(let n=1;n<=limit;n++)for(let i=0;i<coins.length;i++)if(n>=coins[i]&&dp[n-coins[i]]+1<dp[n]){dp[n]=dp[n-coins[i]]+1;prev[n]=i;}
      let n=needed;while(dp[n]===2147483647&&n<limit)n++;
      while(n>0){const i=prev[n],size=sizes[i];counts.set(size,(counts.get(size)||0)+1);n-=coins[i];}
    }
    const lines=[...counts].sort((a,b)=>b[0]-a[0]).map(([size,count])=>({size,count,name:names[sizes.indexOf(size)]||size+'-piece pack'}));
    const purchased=lines.reduce((n,l)=>n+l.size*l.count,0),packs=lines.reduce((n,l)=>n+l.count,0);
    return {lines,purchased,packs,surplus:purchased-target};
  }
  function matchedWood(family, fallback, stack, backing, minEmbed, maxEmbed = 2, tip = .125, endClearance = 0) {
    const minimum = stack + minEmbed + tip;
    const maximum = Math.min(stack + backing - endClearance, stack + maxEmbed + tip);
    // Filter the complete catalogue before selecting. A too-long first match
    // no longer prevents checking a shorter alternative in MultiGrip.
    const fit = f => products.filter(p => p.available !== false && p.family === f && p.length >= minimum - EPS && p.length <= maximum + EPS)
      .sort((a, b) => a.length - b.length || Number(a.diameter.slice(1)) - Number(b.diameter.slice(1)));
    const primary = fit(family), secondary = fallback ? fit('MultiGrip') : [];
    return { product: primary[0] || secondary[0] || null, minimum, maximum,
      fallback: !primary.length && !!secondary.length, profile: { stack, backing, minEmbed, maxEmbed, tip, endClearance } };
  }
  function countTrim(length, stock, spacing) {
    const full = Math.floor((length + EPS) / stock), remainder = Math.max(0, length - full * stock);
    return full * (ceil(stock * 12 / spacing) + 1) + (remainder > EPS ? ceil(remainder * 12 / spacing) + 1 : 0);
  }
  function calculate(scene, supplied = {}) {
    if (!defaults[scene]) return fail('scene', 'Choose a project.');
    const allowed = [...Object.keys(defaults[scene]), 'waste', 'extra', 'packMode'];
    const relevant = Object.fromEntries(Object.entries(supplied || {}).filter(([k]) => allowed.includes(k)));
    const x = { ...defaults[scene], ...(source.defaults?.[scene] || {}), waste: source.waste ?? 10, extra: 0, packMode: 'mix', ...relevant };
    const required = {
      wood: ['length', 'width', 'boardWidth', 'thickness', 'spacing', 'backing'],
      composite: ['length', 'width', 'boardWidth', 'thickness', 'spacing', 'backing'],
      steel: ['length', 'width', 'boardWidth', 'thickness', 'spacing', 'steelThickness'],
      hiddenwood: ['length', 'width', 'boardWidth', 'thickness', 'spacing', 'backing', 'clipSeat'],
      hiddensteel: ['length', 'width', 'boardWidth', 'thickness', 'spacing', 'steelThickness', 'clipSeat'],
      fence: ['length', 'height', 'boardWidth', 'thickness', 'rails', 'screwsPerPoint', 'backing'],
      horizontal: ['length', 'height', 'boardWidth', 'thickness', 'spacing', 'backing', 'boardLength', 'screwsPerPoint'],
      trim: ['length', 'boardWidth', 'thickness', 'boardLength', 'spacing', 'screwsPerPoint', 'backing'],
      join: ['connections', 'screwsPerPoint', 'thickness', 'backing'],
      faceframe: ['connections', 'screwsPerPoint', 'thickness', 'backing'],
      cabinet: ['units', 'studs', 'screwsPerPoint', 'thickness', 'backing'],
      structural: ['connections', 'screwsPerPoint']
    }[scene];
    for (const k of [...required, 'waste', 'extra']) {
      if (x[k] === '' || x[k] === null || !Number.isFinite(Number(x[k])) || Number(x[k]) < (['waste', 'extra'].includes(k) ? 0 : EPS)) return fail(k, 'Enter a valid ' + k.replace(/([A-Z])/g, ' $1').toLowerCase() + '.');
      x[k] = Number(x[k]);
      if (x[k] > 10000) return fail(k, 'Split large projects into smaller areas.');
      if (['connections', 'units', 'studs', 'rails', 'screwsPerPoint', 'extra'].includes(k) && !Number.isInteger(x[k])) return fail(k, 'Use a whole number for ' + k + '.');
    }
    if (x.waste > 100) return fail('waste', 'Use a spare allowance between 0 and 100%.');
    if (!['mix', 'Handy Pack', 'Homepak', 'Engineer Tub'].includes(x.packMode)) return fail('packMode', 'Choose a pack option.');
    if ('allowStandardHead' in x) x.allowStandardHead = x.allowStandardHead === true || x.allowStandardHead === 'true';
    for (const k of ['gap', 'boardLength', 'wallLayer']) if (k in x) {
      if (x[k] === '' || x[k] === null || !Number.isFinite(Number(x[k])) || Number(x[k]) < 0) return fail(k, 'Enter a valid ' + k + '.');
      x[k] = Number(x[k]);
    }
    const hidden = scene.startsWith('hidden'), metal = ['steel', 'hiddensteel'].includes(scene);
    let family, base, fit, pending = false, note = '', auxiliary = [], metadata = {};
    if (['wood', 'composite', 'steel', 'hiddenwood', 'hiddensteel'].includes(scene)) {
      const rows = Math.max(1, ceil((x.width * 12 + x.gap) / (x.boardWidth + x.gap)));
      const supports = ceil(x.length * 12 / x.spacing) + 1;
      const seams = x.boardLength ? Math.max(0, ceil(x.length / x.boardLength) - 1) : 0;
      const endPositions = supports + seams;
      metadata = { area: x.length * x.width, rows, supports, seamsPerRow: seams, endPositions,
        layout: 'Straight perpendicular boards. Aligned seams require separate end support. Additional seam end positions counted.' };
      if (hidden) {
        base = Math.max(0, rows - 1) * endPositions;
        family = metal ? 'DeckMVP / Metal' : 'DeckMVP / Wood';
        auxiliary = [{ label: 'Starter / finish clips', count: 2 * endPositions, note: 'Separate perimeter module' }];
        metadata.clipMaterial = x.clipMaterial;
        if (metal) {
          const stack = x.clipSeat + x.steelThickness;
          fit = { product: products.find(p => p.available !== false && p.family === family && p.length >= stack + .5 - EPS) || null,
            minimum: stack + .5, profile: { clipSeat: x.clipSeat, steelThickness: x.steelThickness, provisionalPointAllowance: .5 } };
        } else fit = matchedWood(family, false, x.clipSeat, x.backing, 1, 2);
        pending = true;
        note = metal ? 'Confirm clip fit and steel drilling range.' : 'Confirm the clip and matching screw fit.';
      } else {
        base = rows * endPositions * 2;
        family = scene === 'composite' ? 'DeckPro / Composite' : metal ? 'DeckPro / Metal' : 'DeckPro / Wood';
        if (metal) {
          const stack = x.thickness + x.steelThickness;
          fit = { product: products.find(p => p.available !== false && p.family === family && p.length >= stack + .5 - EPS) || null,
            minimum: stack + .5, profile: { thickness: x.thickness, steelThickness: x.steelThickness, provisionalPointAllowance: .5 } };
          pending = true; note = 'Confirm steel thickness and drilling range.';
        } else fit = matchedWood(family, scene === 'wood', x.thickness, x.backing, source.woodEmbed ?? 1.25, source.maxEmbed ?? 2);
      }
    } else if (scene === 'fence') {
      const pickets = ceil((x.length * 12 + x.gap) / (x.boardWidth + x.gap));
      base = pickets * x.rails * x.screwsPerPoint; family = 'MultiGrip';
      fit = matchedWood(family, false, x.thickness, x.backing, Math.min(1, x.backing * .65), 1.5);
      metadata = { pickets, rails: x.rails, screwsPerPoint: x.screwsPerPoint, area: x.length * x.height,
        scope: 'Pickets only. Post / rail connections and gates excluded.' };
    } else if (scene === 'horizontal') {
      const rows = ceil((x.height * 12 + x.gap) / (x.boardWidth + x.gap));
      const supports = ceil(x.length * 12 / x.spacing) + 1;
      const seams = Math.max(0, ceil(x.length / x.boardLength) - 1);
      base = rows * (supports + seams) * x.screwsPerPoint; family = 'MultiGrip';
      fit = matchedWood(family, false, x.thickness, x.backing, 1, 1.75);
      metadata = { rows, supports, seamsPerRow: seams, area: x.length * x.height,
        scope: 'Boards only. Seams require approved support; duplicate end positions included.' };
    } else if (scene === 'trim') {
      const stations = countTrim(x.length, x.boardLength, x.spacing);
      base = stations * x.screwsPerPoint; family = 'FinishPro / Trim Head';
      fit = matchedWood(family, x.allowStandardHead, x.thickness + x.wallLayer, x.backing, .75, 1.5);
      metadata = { stations, length: x.length, screwsPerPoint: x.screwsPerPoint, wallLayer: x.wallLayer,
        scope: 'Editable trim schedule; each piece has end stations. Not a code fastening schedule.' };
    } else if (scene === 'join' || scene === 'faceframe') {
      base = x.connections * x.screwsPerPoint;
      family = scene === 'faceframe' ? 'FinishPro / Trim Head' : 'MultiGrip';
      const min = Math.min(scene === 'faceframe' ? .5 : .75, x.backing * .5);
      fit = matchedWood(family, scene === 'faceframe' && x.allowStandardHead, x.thickness, x.backing, min, 1.5, .125, .125);
      metadata = { connections: x.connections, screwsPerPoint: x.screwsPerPoint,
        scope: 'Ordinary non-load-rated joining. Quantity schedule is user supplied.' };
    } else {
      family = scene === 'cabinet' ? 'FinishPro / CabiMax' : 'FrameMax';
      base = scene === 'cabinet' ? x.units * x.studs * x.screwsPerPoint : x.connections * x.screwsPerPoint;
      const chosen = products.find(p => p.key === x.approvedProduct && p.family === family);
      if (chosen) fit = { product: chosen, minimum: chosen.length, profile: { selection: 'Exact screw copied from customer approved schedule' } };
      else if (scene === 'cabinet') fit = matchedWood(family, false, x.thickness + x.wallLayer, x.backing, 1.25, 2);
      else fit = { product: null, minimum: null };
      pending = !chosen;
      note = chosen ? '' : scene === 'cabinet' ? 'Confirm the rated cabinet mounting screw.' : 'Send us the size on your approved connection plan.';
      metadata = { units: x.units, studs: x.studs, connections: x.connections, screwsPerPoint: x.screwsPerPoint,
        scope: 'Count only. No capacity design, structural substitution or wall mounting approval inferred.' };
    }
    const target = ceil((base + x.extra) * (100 + x.waste) / 100);
    if (!Number.isSafeInteger(target) || target > 10000000) return fail('length', 'Check your measurement units or split this large project into smaller areas.');
    const product = fit.product;
    if (product) family = product.family;
    if (!product) { pending = true; note = 'Request a suitable size for this connection.'; }
    metadata.fit = fit; metadata.contingency = x.waste; metadata.inputs = x;
    const pack = planPacks(target, family, x.packMode, product);
    if(!pack.lines.length){pending=true;note='Request a suitable pack for this project.';}
    const item = { family, product, size: product ? product.size : 'Suitable size requested', base, target,
      kind: hidden ? 'clips with matched screws' : 'screws', pack, confirmationRequired: pending,
      clipMaterial: hidden ? x.clipMaterial : null };
    return { status: pending ? 'review' : 'matched', scene, sceneLabel: scenes[scene], items: [item], auxiliary,
      note, fallback: !!fit.fallback, metadata, canApply: !!product && !pending && target > 0 };
  }
  const api = { products, ranges, scenes, defaults, calculate, planPacks, packOptions, countTrim, matchedWood, inches };
  return api;

}
const api = { createEngine, normalizeCatalog, parseDimension, purchaseEntries };
if(typeof module !== "undefined" && module.exports) module.exports=api; else window.VertexaFastenerCore=api;
})();
